import { Controller } from 'stimulus'
import { definitionOf } from 'classes/async-helpers'

/**
 * Manages js for the short refinance application edit partial (StimulusJS
 * controller)
 *
 * @class RefinanceApplicationController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'currentPage',
    'formType',
    'input',
    'next',
    'previous',
    'productType',
    'slug',
    'step',
    'typ'
  ]

  /**
   * @function controllers.ShortRefinanceApplicationController.initialize
   * @memberof controllers
   * @description triggers "load" event type actions.
   *
   * @return {undefined} manipulates the dom.
   *
   */
  initialize() {
    this.stepEvent = new CustomEvent('turbolinks:load')
    this._sendGTMDataLayer()
    this._updateSessionLevelLogging()
    this._sendSessionData()
    this._updateGetParams()
    this._updateStepId()
  }

  /**
   * @function Controllers.ShortRefinanceApplicationController.next
   * @memberof Controllers
   * @description Moves the multi-step form to the next step, gathering the
   * inputs for the step from the data targets 'input' and sends it off to the backend
   * along with marketing values.
   *
   * @param {Event} event - User initiated click event representing the desire
   * to move forward in the form.
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  next(event) {
    event.preventDefault()
    const inputs = {}
    this.inputTargets.forEach(input => {
      if ((input.type === 'radio' && input.checked) || input.type !== 'radio') {
        inputs[input.name] = input.value
      }
    })

    const data = JSON.stringify({ short_refinance_application: inputs })
    this.loadStep(data)
  }

  /**
   * @function Controllers.ShortRefinanceApplicationController.prev
   * @memberof Controllers
   * @description Moves the multi-step form to the previous step and updates the
   * UI.
   *
   * @param {Event} event - User initiated click event representing the desire
   * to move forward in the form.
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  prev(event) {
    event.preventDefault()
    const slug = this.data.get('slug')
    const csrfToken = this.data.get('csrf-token')
    fetch(`/apply/short-refinance/steps/${slug}`, {
      method:      'GET',
      credentials: 'same-origin',
      headers:     {
        'content-type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      mode: 'cors'
    })
      .then(response => response.text())
      .then(html => {
        this.stepTarget.innerHTML = html
        this._updateSessionLevelLogging()
        this._sendGTMDataLayer()
        this._sendSessionData()
        this._updateGetParams()
        this._updateStepId()
      })
      .then(() => {
        document.dispatchEvent(this.stepEvent)
      })
  }

  /**
   * @function Controllers.ShortRefinanceApplicationController.loadStep
   * @memberof Controllers
   * @description Sends the data to the back-end and updates the UI.
   *
   * @param {JSON} data - JSON form of form info to be sent to the backend
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  loadStep(data) {
    const slug = this.data.get('slug')
    const csrfToken = this.data.get('csrf-token')
    fetch(`/apply/short-refinance/${slug}`, {
      method:      'PATCH',
      body:        data,
      credentials: 'same-origin',
      headers:     {
        'content-type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      mode: 'cors'
    })
      .then(response => response.text())
      .then(html => {
        this.stepTarget.innerHTML = html
        this._maybeRemoveSidebar()
        this._updateSessionLevelLogging()
        this._sendGTMDataLayer()
        this._sendSessionData()
        this._updateGetParams()
        this._updateStepId()
        window.scroll({ top: 0, behavior: 'smooth' })
        document.getElementsByClassName('goldy-short__card')[0].querySelectorAll('input:not([type=hidden]),button,select,textarea')[0].focus()
      })
      .then(() => {
        document.dispatchEvent(this.stepEvent)
      })
  }

  /**
   * @function Controllers.ShortRefinanceApplicationController._maybeRemoveSidebar
   * @memberof Controllers
   * @description Triggers global event to remove sidebar on typ
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  _maybeRemoveSidebar() {
    if (this.hasTypTarget) {
      window.dispatchEvent(new CustomEvent('sidebar:remove'))
    }
  }

  /**
   * @function Controllers.ShortRefinanceApplicationController._sendGTMDataLayer
   * @memberof Controllers
   * @description Adds data to the GTM DataLayer
   * @private
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  _sendGTMDataLayer() {
    window.dataLayer = window.dataLayer || []
    const page = this.currentPageTarget.value
    const applicationStart = (page === 'approximate_value').toString()
    const applicationComplete = (page === 'form_received').toString()

    window.dataLayer.push({
      'application_start':    applicationStart,
      'application_complete': applicationComplete,
      'current_page':         page,
      'session_uuid':         sessionStorage.getItem('sessionUUID'),
      'event':                'turboPageLoad',
      'product_type':         'refinance',
      'form_type':            'short'
    })
  }

  /**
   * @function Controllers.ShortRefinanceApplicationController._sendSessionData
   * @memberof Controllers
   * @description Sends session data as soon as it becomes available
   * @private
   *
   * @param {Object} sessionData
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  async _sendSessionData(sessionData) {
    try {
      await definitionOf('session_data', 2000)
      sendSessionData(sessionData) // eslint-disable-line no-undef
    } catch (error) {
      return
    }
  }

  /**
   * @function Controllers.ShortRefinanceApplicationController._setSessionData
   * @memberof Controllers
   * @description Sets session data as soon as it becomes available
   * @private
   *
   * @param {String} key
   * @param {String} value
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  async _setSessionData(key, value) {
    try {
      await definitionOf('setSessionData', 2000)
      setSessionData(key, value) // eslint-disable-line no-undef
    } catch (error) {
      return
    }
  }

  /**
   * @function Controllers.ShortRefinanceApplicationController._updateSessionLevelLogging
   * @memberof Controllers
   * @description Sets data for session.
   * @private
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  _updateSessionLevelLogging() {
    const page = this.currentPageTarget.value
    const applicationStart = (page === 'approximate_value').toString()
    const applicationComplete = (page === 'form_received').toString()
    const formType = this.formTypeTarget.value
    const productType = this.productTypeTarget.value
    const slug = this.slugTarget.value
    this._setSessionData('application_complete', applicationComplete)
    this._setSessionData('application_start', applicationStart)
    this._setSessionData('form_type', formType)
    this._setSessionData('product_type', productType)
    this._setSessionData('slug', slug)
    this._setSessionData('step_name', page)
  }

  /**
   * @function Controllers.ShortRefinanceApplicationController._updateGetParams
   * @memberof Controllers
   * @description Push get params onto url.
   * @private
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  _updateGetParams() {
    const url = new URL(window.location.href)
    url.searchParams.set('current', this.currentPageTarget.value)
    window.history.replaceState(null, '', url)
  }

  /**
   * @function Controllers.ShortRefinanceApplicationController._updateStepId
   * @memberof Controllers
   * @description Update Step container id to current page identifier
   * @private
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  _updateStepId() {
    this.stepTarget.setAttribute('id', this.currentPageTarget.value.replace(/_/, '-'))
  }
}
